/* Hebrew live suggestions use Shopify's regular search + Section Rendering.
   Native predictive search remains available for supported storefront locales. */
(() => {
  'use strict';
  let instanceId = 0;
  const escapeHtml = value => String(value).replace(/[&<>"']/g, c => ({'&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;'}[c]));
  const config = JSON.parse(document.getElementById('swaggy-search-config').textContent);

  window.SwaggySearch = {
    create(type, desktopLimit, mobileLimit, defaultProductType) {
      let controller;
      let requestId = 0;
      let root;
      let input;
      let results;
      const cache = new Map();
      const id = `swaggy-search-${++instanceId}`;
      return {
        open_search: '', t: null, result: '', query: '', openResults: false,
        productTypeSelected: defaultProductType, showSuggest: false, loading: false,
        activeIndex: -1, searchActive: false,
        init() {
          root = this.$el;
          input = root.querySelector('input[name="q"]');
          results = root.querySelector('[data-predictive-search]');
          if (input && results) {
            if (!results.id) results.id = id;
            input.setAttribute('aria-controls', results.id);
            results.setAttribute('role', 'listbox');
            results.setAttribute('aria-label', input.getAttribute('aria-label') || 'Search');
          }
        },
        destroy() { clearTimeout(this.t); controller?.abort(); requestId++; },
        open(refName) {
          this.$refs[refName]?.classList.remove('popup-hidden');
          this.$nextTick(() => input?.focus());
        },
        dismiss() {
          clearTimeout(this.t);
          controller?.abort();
          requestId++;
          this.loading = false;
          this.openResults = false;
          this.showSuggest = false;
          this.activeIndex = -1;
          input?.removeAttribute('aria-activedescendant');
        },
        close(refName) { this.$refs[refName]?.classList.add('popup-hidden'); this.dismiss(); this.searchActive = false; },
        clearSearch() {
          if (!input) return;
          input.value = '';
          this.query = '';
          this.result = '';
          this.dismiss();
          this.searchActive = true;
          input.focus();
        },
        closeSearch() {
          this.dismiss();
          this.searchActive = false;
          input?.blur();
          root.querySelector('.btn-search')?.focus({preventScroll:true});
        },
        keyUp(event) {
          if (event?.isComposing) return;
          this.searchActive = true;
          this.query = (event?.target || input || this.$el).value.trim();
          clearTimeout(this.t);
          controller?.abort();
          requestId++;
          this.loading = false;
          this.result = '';
          this.dismiss();
          if (this.query.length < 2) { this.showSuggest = this.query.length === 0; return; }
          this.t = setTimeout(() => this.getSearchResult(this.query), 300);
        },
        searchUrl(query, withSection = false) {
          const url = new URL(config.searchUrl, window.location.origin);
          const filter = this.productTypeSelected && this.productTypeSelected !== defaultProductType
            ? `product_type:${JSON.stringify(this.productTypeSelected)} AND ` : '';
          url.searchParams.set('q', filter + query);
          url.searchParams.set('type', 'product');
          url.searchParams.set('options[prefix]', 'last');
          if (withSection) url.searchParams.set('section_id', 'swaggy-search-results');
          return url;
        },
        message(label, query) {
          return `<div class="swaggy-search-results"><p role="status">${escapeHtml(label)}</p><a class="swaggy-search-all" data-swaggy-option role="option" aria-selected="false" tabindex="-1" href="${escapeHtml(this.searchUrl(query))}">${escapeHtml(config.viewAll)}</a></div>`;
        },
        render(markup) {
          this.result = markup;
          this.activeIndex = -1;
          input?.removeAttribute('aria-activedescendant');
          this.$nextTick(() => this.options().forEach((el, i) => { el.id = `${id}-option-${i}`; }));
        },
        options() {
          return results ? [...results.querySelectorAll('[data-swaggy-option], li[role="option"] a')].filter(el => el.getClientRects().length) : [];
        },
        async getSearchResult(query) {
          controller?.abort();
          controller = new AbortController();
          const token = ++requestId;
          this.openResults = true;
          this.showSuggest = false;
          const limit = window.innerWidth > 767 ? desktopLimit : mobileLimit;
          const key = JSON.stringify([query, this.productTypeSelected, limit, config.fallback]);
          if (cache.has(key)) { this.loading = false; this.render(cache.get(key)); return; }
          this.loading = true;
          this.render(this.message(config.loading, query));
          let url = this.searchUrl(query, true);
          if (!config.fallback) {
            url = new URL(`${window.Shopify.routes.root}search/suggest`, window.location.origin);
            url.search = new URLSearchParams({q: this.searchUrl(query).searchParams.get('q'), 'resources[type]':type,
              'resources[limit]':limit, section_id:'predictive-search'}).toString();
          }
          try {
            const response = await fetch(url, {signal:controller.signal, credentials:'same-origin'});
            if (!response.ok) throw new Error(`Search response ${response.status}`);
            const text = await response.text();
            const doc = new DOMParser().parseFromString(text, 'text/html');
            const section = doc.getElementById(`shopify-section-${config.fallback ? 'swaggy-search-results' : 'predictive-search'}`);
            if (!section?.innerHTML.trim()) throw new Error('Search section unavailable');
            if (token !== requestId) return;
            cache.set(key, section.innerHTML);
            if (cache.size > 30) cache.delete(cache.keys().next().value);
            this.render(section.innerHTML);
          } catch (error) {
            if (token === requestId && error.name !== 'AbortError') this.render(this.message(config.error, query));
          } finally {
            if (token === requestId) this.loading = false;
          }
        },
        searchKeydown(event) {
          if (event.key === 'Escape') { event.preventDefault(); this.closeSearch(); return; }
          if (!this.openResults) return;
          const options = this.options();
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            if (!options.length) return;
            event.preventDefault();
            this.activeIndex = this.activeIndex < 0 ? (event.key === 'ArrowDown' ? 0 : options.length - 1)
              : (this.activeIndex + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
            options.forEach((el, i) => el.setAttribute('aria-selected', String(i === this.activeIndex)));
            input?.setAttribute('aria-activedescendant', options[this.activeIndex].id);
            options[this.activeIndex].scrollIntoView({block:'nearest'});
          } else if (event.key === 'Enter' && this.activeIndex >= 0 && options[this.activeIndex]) {
            event.preventDefault();
            options[this.activeIndex].click();
          } else if (event.key === 'Tab') { this.dismiss(); }
        },
        setProductType(value, id) {
          this.productTypeSelected = value;
          const field = root.querySelector(`[id="${CSS.escape(id)}"]`);
          if (field) field.value = value;
          if (this.query.length > 1) this.getSearchResult(this.query);
        },
        focusForm() {
          this.searchActive = true;
          this.query = (input?.value || '').trim();
          if (this.query.length > 1) this.getSearchResult(this.query);
          else this.showSuggest = !this.query;
        }
      };
    }
  };
})();
